<template>
  <div>
    <transition
      enter-active-class="transition-opacity ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        data-testid="cart-drawer-scrim"
        class="fixed inset-0 z-999 bg-gray-900 bg-opacity-75 xl:hidden"
        @click="$emit('close')"
      ></div>
    </transition>

    <transition name="cart-drawer" @after-enter="$emit('opened')">
      <aside
        v-if="open"
        data-testid="cart-drawer"
        role="dialog"
        :aria-modal="docked ? 'false' : 'true'"
        aria-label="Cart"
        class="fixed inset-0 z-1000 flex w-full flex-col bg-white md:inset-y-0 md:left-auto md:right-0 md:w-[400px] md:border-l md:border-gray-200 md:shadow-xl"
      >
        <div class="flex items-center gap-3 border-b border-gray-200 px-4 py-4">
          <h2 class="text-xl font-bold tracking-tighter text-gray-brand-900">Your cart</h2>
          <div class="ml-auto flex items-center gap-3">
            <span v-if="itemCount > 0" data-testid="cart-drawer-count" class="text-sm text-gray-brand-600">
              {{ itemCount }} item{{ itemCount === 1 ? '' : 's' }}
            </span>
            <button
              data-testid="cart-drawer-close"
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-brand-200 text-gray-brand-800 hover:bg-gray-brand-300"
              aria-label="Close cart"
              @click="$emit('close')"
            >
              <XIcon class="h-5 w-5" />
            </button>
          </div>
        </div>

        <slot name="alerts"></slot>
        <div v-if="$slots.progress">
          <slot name="progress"></slot>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <template v-if="itemCount > 0">
            <slot name="items"></slot>
            <div class="px-6 pb-4 pt-2 xl:hidden">
              <button
                data-testid="cart-drawer-add-more"
                class="w-full rounded-lg border-2 border-brand-800 px-4 py-3 text-sm font-bold text-brand-900 transition-colors hover:bg-brand-100"
                @click="$emit('add-more')"
              >
                + Add More Items
              </button>
            </div>
          </template>
          <div
            v-else
            data-testid="cart-drawer-empty"
            class="flex h-full flex-col items-center justify-center gap-3 px-6"
          >
            <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
              <ShoppingBagIcon class="h-6 w-6" />
            </div>
            <p class="text-sm text-gray-brand-600">Your cart is empty</p>
            <button class="text-sm font-medium text-brand-800 underline" @click="$emit('add-more')">
              Browse the menu
            </button>
          </div>
        </div>

        <div class="flex-shrink-0" :class="{ 'border-t border-gray-200': !(showPrices && totals && itemCount > 0) }">
          <div class="max-h-[45vh] overflow-y-auto">
            <OrderSummaryTotals
              v-if="showPrices && totals && itemCount > 0"
              :full-bleed-dividers="true"
              :subtotal-formatted="totals.subtotalFormatted"
              :discount="totals.discount"
              :fulfillment-discount="totals.fulfillmentDiscount"
              :delivery-formatted="totals.deliveryFormatted"
              :delivery-original-formatted="totals.deliveryOriginalFormatted"
              :delivery-label="totals.deliveryLabel"
              :taxes="totals.taxes"
              :tip="totals.tip"
              :credits="totals.credits"
              :todays-total-formatted="totals.todaysTotalFormatted"
            />
            <div v-if="fulfillmentLine" class="flex items-center gap-2 px-4 pb-2 text-sm text-gray-brand-700">
              <TruckIcon v-if="!isPickup" class="h-4 w-4" />
              <ShoppingBagIcon v-else class="h-4 w-4" />
              <span data-testid="cart-drawer-fulfillment">{{ fulfillmentLine }}</span>
            </div>
          </div>
          <div
            class="relative px-4 pb-4"
            :class="{ 'pt-3': !fulfillmentLine && !(showPrices && totals && itemCount > 0) }"
          >
            <transition
              enter-active-class="transition-opacity ease-out duration-200"
              enter-from-class="opacity-0"
              leave-active-class="transition-opacity ease-in duration-200"
              leave-to-class="opacity-0"
            >
              <div
                v-if="note"
                data-testid="cart-drawer-note"
                class="pointer-events-none absolute inset-x-4 bottom-full z-10 mb-2 flex justify-center"
              >
                <Toast :message="note" type="info" />
              </div>
            </transition>
            <PrimaryButton
              class="w-full"
              :size="'lg'"
              :streaking="itemCount > 0 && !proceedDisabled"
              :active="itemCount > 0 && !proceedDisabled"
              :disabled="proceedDisabled"
              @click="$emit('proceed')"
            >
              {{ proceedLabel }}
              <template #right-icon><ArrowRightIcon class="h-4" /></template>
            </PrimaryButton>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
let strftime = require('strftime')
import { XIcon, ArrowRightIcon, ShoppingBagIcon } from '@heroicons/vue/solid'
import { TruckIcon } from '@heroicons/vue/outline'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import Toast from '@/components/Molecules/Toast'
import OrderSummaryTotals from '@/components/Checkout/OrderSummaryTotals'
import { fulfillmentTitleFor } from '@/utils/fulfillmentTypes'
import { CART_DRAWER_DOCKED_QUERY, isCartDrawerDocked } from '@/utils/cartDrawerDock'

export default {
  name: 'CartDrawer',
  components: { XIcon, ArrowRightIcon, ShoppingBagIcon, TruckIcon, PrimaryButton, Toast, OrderSummaryTotals },
  props: {
    open: { type: Boolean, required: true },
    itemCount: { type: Number, default: 0 },
    showPrices: { type: Boolean, default: true },
    totals: { type: Object, default: null },
    proceedLabel: { type: String, default: 'Proceed to checkout' },
    proceedDisabled: { type: Boolean, default: false },
    // A short note floated just above the proceed button, for when the drawer is a sheet over the page
    // and the page's bottom bar is out of sight (BOT-1748).
    note: { type: String, default: null }
  },
  // `opened` once the drawer has finished sliding in, so its lines are where they will stay.
  emits: ['close', 'proceed', 'add-more', 'opened'],
  data() {
    return { docked: isCartDrawerDocked() }
  },
  computed: {
    fulfillmentSlot() {
      return this.$store.getters.fulfillmentSlot
    },
    fulfillmentMethodType() {
      return this.$store.getters.fulfillmentMethod?.attributes?.type
    },
    isPickup() {
      return this.fulfillmentMethodType === 'PickupFulfillmentMethod'
    },
    fulfillmentLine() {
      const date = this.fulfillmentSlot?.attributes?.fulfillment_date
      if (!date || !this.fulfillmentMethodType) return null
      const label = fulfillmentTitleFor(this.fulfillmentMethodType)
      return `${label} · ${strftime('%A, %B %-d', new Date(date + 'T12:00:00.000Z'))}`
    }
  },
  watch: {
    open(isOpen) {
      this.syncBodyScrollLock(isOpen)
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    this.dockedMediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia(CART_DRAWER_DOCKED_QUERY) : null
    this.dockedMediaQuery?.addEventListener('change', this.syncDocked)
    this.syncDocked()
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    this.dockedMediaQuery?.removeEventListener('change', this.syncDocked)
    document.body.style.overflow = ''
  },
  methods: {
    onKeydown(event) {
      // A product modal opened over the drawer handles Escape first and marks it handled; that press
      // was only meant for the modal.
      if (event.key === 'Escape' && this.open && !event.defaultPrevented) this.$emit('close')
    },
    syncDocked() {
      this.docked = Boolean(this.dockedMediaQuery?.matches)
      this.syncBodyScrollLock(this.open)
    },
    syncBodyScrollLock(isOpen) {
      // Docked at the edge, the page stays scrollable; as a sheet over it, the page is locked behind
      // the scrim.
      document.body.style.overflow = isOpen && !this.docked ? 'hidden' : ''
    }
  }
}
</script>

<style scoped>
.cart-drawer-enter-active,
.cart-drawer-leave-active {
  transition: transform 220ms ease-out;
}
.cart-drawer-enter-from,
.cart-drawer-leave-to {
  transform: translateY(100%);
}
@media (min-width: 768px) {
  .cart-drawer-enter-from,
  .cart-drawer-leave-to {
    transform: translateX(105%);
  }
}
</style>
